import { useEffect, useState } from 'react';

/**
 * The ONE breakpoint the layout switches on, read from the token
 * `--breakpoint-desktop` so CSS (`md:`) and JS cannot drift apart.
 *
 * Layout that can be expressed in CSS should be expressed in CSS. This hook is
 * for the cases where the two viewports need genuinely different COMPONENTS -
 * a Dialog that becomes a bottom sheet, a table that becomes a card list -
 * rather than the same DOM with different padding. That distinction is exactly
 * what Twenty got wrong (one 768px breakpoint, desktop grid on a phone).
 */
const FALLBACK_QUERY = '(min-width: 48rem)';

function desktopQuery(): string {
  const token = getComputedStyle(document.documentElement)
    .getPropertyValue('--breakpoint-desktop')
    .trim();
  return token.length > 0 ? `(min-width: ${token})` : FALLBACK_QUERY;
}

function matches(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // Test environments without matchMedia get the mobile layout: the narrow
    // path is the one we must not break.
    return false;
  }
  return window.matchMedia(desktopQuery()).matches;
}

export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(matches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const list = window.matchMedia(desktopQuery());
    const onChange = () => setIsDesktop(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}
